import React, { useState, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { makeStyles } from '@mui/styles';

import PlusIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteIcon from '@mui/icons-material/Close';
import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';

/*
Call it with
  <KReorderableList
    id={<value to be returned in onSave() indicating which list is being reordered>} // allows caller to use this to edit multiple lists
    addable={T/F} // whether to include the UI to add new items
    items={[<initial list of objects {id: x, text: y, deletable: T/F} to reorder>]}
    onSave={<function(id, orderedItems) to call when user clicks Save button>}
  />
*/

const createReorderableListStyles = makeStyles((theme) => ({
  root: {},
  newItemField: {
    marginBottom: '12px',
    width: '320px',
  },
  newItemButton: {
    marginTop: '10px',
    marginLeft: '20px',
  },
  plusIcon: {
    color: 'white',
    transform: 'scale(1.5)',
  },
  addButtonText: {
    color: 'white',
    fontWeight: 600,
  },
  listContainer: {
    padding: '0 10px 10px 10px',
    width: '100%',
    maxWidth: 500,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid gray',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#ffd2d2',
    marginTop: '15px',
    cursor: 'move',
  },
  menuIcon: {
    marginTop: '8px',
    marginLeft: '12px',
    marginRight: '8px',
  },
  itemText: {
    fontWeight: 'bold',
    fontSize: '1.17em',
    marginBlockStart: '0.5em',
    marginBlockEnd: '0.5em',
  },
  deleteIcon: {
    display: 'inline-block',
    marginLeft: 'auto',
    marginRight: '4px',
    marginTop: '4px',
    cursor: 'pointer',
  },
  noDisplay: {
    display: 'none',
    marginTop: '4px',
  },
  saveOrCancelButtonDiv: {
    maxWidth: 500,
    textAlign: 'right',
    marginTop: '10px',
  },
}));

const KReorderableList = ({ id, addable, items, onSave }) => {
  const classes = createReorderableListStyles();
  const [reorderableItems, setReorderableItems] = useState(items);
  const [newItemText, setNewItemText] = useState('');
  const draggedItemIndex = useRef();
  const draggedOverItemIndex = useRef();

  useEffect(() => {
    setReorderableItems(items);
  }, [items]);

  const onDragStart = (event, index) => {
    draggedItemIndex.current = index;
  };

  const onDragEnter = (event, index) => {
    draggedOverItemIndex.current = index;
  };

  const onDragEnd = (event, index) => {
    doSort();
  };

  const doSort = () => {
    const _reorderableItems = [...reorderableItems];
    const draggedItemContent = _reorderableItems.splice(draggedItemIndex.current, 1)[0];
    _reorderableItems.splice(draggedOverItemIndex.current, 0, draggedItemContent);
    draggedItemIndex.current = null;
    draggedOverItemIndex.current = null;
    setReorderableItems(_reorderableItems);
  };

  const addNewItem = () => {
    const newItem = { id: 0, text: newItemText, deletable: true };
    const newList = [...reorderableItems, newItem];
    setReorderableItems(newList);
    setNewItemText('');
  };

  const deleteItem = (item) => {
    const otherItems = reorderableItems.filter((i) => i !== item);
    setReorderableItems(otherItems);
  };

  const cancelChanges = () => {
    setReorderableItems(items);
  };

  return (
    <Box className={classes.root} desplay="flex" gap="10px">
      {addable && (
        <div>
          <CrmTextField
            className={classes.newItemField}
            label="New Item"
            value={newItemText}
            onChange={(event) => setNewItemText(event.target.value)}
          />
          <Button
            className={classes.newItemButton}
            color="primary"
            variant="contained"
            disabled={!newItemText}
            startIcon={<PlusIcon className={classes.plusIcon} />}
            onClick={addNewItem}
          >
            <span className={classes.addButtonText}>Add New Item</span>
          </Button>
        </div>
      )}
      <div className={classes.listContainer}>
        {reorderableItems.map((item, index) => {
          return (
            <div
              key={index}
              className={classes.listItem}
              draggable
              onDragStart={(e) => onDragStart(e, index)}
              onDragEnter={(e) => onDragEnter(e, index)}
              onDragEnd={(e) => onDragEnd(e, index)}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className={classes.menuIcon}>
                <MenuIcon />
              </div>
              <div className={classes.itemText}>{item.text}</div>
              <div className={classes.deleteIcon}>
                <DeleteIcon
                  className={item.deletable ? classes.deleteIcon : classes.noDisplay}
                  onClick={() => deleteItem(item)}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.saveOrCancelButtonDiv}>
        <CrmButton outlined onClick={() => cancelChanges()}>
          Cancel
        </CrmButton>
        <CrmButton filled onClick={() => onSave(id, reorderableItems)}>
          Save
        </CrmButton>
      </div>
    </Box>
  );
};

export default KReorderableList;
